"use client";

import { EditableValue } from "../forms/EditableValue";
import { formatMoneyShort } from "../lib/format";
import { type Provenance, ProvenancePill } from "./ProvenancePill";

export interface SubRowProps {
  /** Sub-item label (e.g. "Cardiology") or "Other (unspecified)". */
  label: string;
  /** FTEs for this sub-item. */
  ftes: number;
  /** Optional avg fully-loaded cost. Null/undefined renders an em-dash. */
  avgCost?: number | null;
  /** Provenance pill for FTEs. Defaults to "user-provided". */
  ftesProvenance?: Provenance;
  /**
   * Read-only mode for the derived "Other (unspecified)" remainder row:
   * no edit affordances, muted styling, no remove button.
   */
  readOnly?: boolean;
  /** Edit the label (editable mode only). */
  onSaveLabel?: (next: string) => void;
  /** Edit FTEs (editable mode only). */
  onSaveFtes?: (next: string) => void;
  /** Edit avg cost (editable mode only). */
  onSaveAvgCost?: (next: string) => void;
  /** Remove this sub-item (editable mode only). */
  onRemove?: () => void;
  /** Auto-focus and edit the FTE amount field. */
  autoFocusFtes?: boolean;
  /** Called after the FTE amount auto-focus request is consumed. */
  onFtesAutoFocus?: () => void;
}

const numCls = "text-right tabular-nums";

/**
 * Indented `<tr>` beneath a RoleRow. Column order matches RoleRow:
 * | (indent) Label | FTEs | FTEs prov | Avg cost | (spacer) | Total |
 */
export function SubRow({
  label,
  ftes,
  avgCost,
  ftesProvenance = "user-provided",
  readOnly = false,
  onSaveLabel,
  onSaveFtes,
  onSaveAvgCost,
  onRemove,
  autoFocusFtes = false,
  onFtesAutoFocus,
}: SubRowProps) {
  const total = avgCost != null ? ftes * avgCost : null;
  const muted = readOnly
    ? "text-(--fg-secondary) italic"
    : "text-(--fg-primary)";
  return (
    <tr className="border-b border-(--border-default) last:border-b-0 text-sm bg-(--bg-canvas)">
      <td className={`py-1.5 pr-3 pl-7 ${muted}`}>
        <div className="flex items-center gap-1.5">
          {readOnly || !onSaveLabel ? (
            <span>{label}</span>
          ) : (
            <EditableValue
              value={label}
              onSave={onSaveLabel}
              ariaLabel="Sub-item label"
            />
          )}
          {!readOnly && onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${label}`}
              className="ml-1 text-(--fg-secondary) hover:text-(--fg-primary) text-xs"
            >
              ✕
            </button>
          ) : null}
        </div>
      </td>
      <td className={`py-1.5 pr-2 ${numCls}`}>
        {readOnly || !onSaveFtes ? (
          <span className="text-(--fg-secondary)">{ftes.toLocaleString()}</span>
        ) : (
          <EditableValue
            value={ftes.toLocaleString()}
            align="right"
            type="number"
            ariaLabel={`FTEs for ${label}`}
            autoFocus={autoFocusFtes}
            onAutoFocus={onFtesAutoFocus}
            onSave={onSaveFtes}
          />
        )}
      </td>
      <td className="py-1.5 pr-3">
        {readOnly ? null : <ProvenancePill kind={ftesProvenance} />}
      </td>
      <td className={`py-1.5 pr-2 ${numCls}`}>
        {readOnly || !onSaveAvgCost ? (
          <span className="text-(--fg-secondary)">
            {avgCost != null ? `$${avgCost.toLocaleString()}` : "—"}
          </span>
        ) : (
          <EditableValue
            value={avgCost != null ? `$${avgCost.toLocaleString()}` : "$0"}
            align="right"
            type="number"
            onSave={onSaveAvgCost}
          />
        )}
      </td>
      <td className="py-1.5 pr-3" />
      <td className={`py-1.5 ${numCls} text-(--fg-secondary)`}>
        {total != null ? formatMoneyShort(total) : "—"}
      </td>
    </tr>
  );
}
